import * as z from "zod";

export type ReviewSkipped = {
  repository: string;
  prNumber: number;
  prUrl: string;
  headSha: string | null;
  failedToStart: boolean;
  /** A review_changes call that failed to start; only [code]smith can ask again with its notes. */
  requestedByAgent: boolean;
  skipReason: string;
  reason: string | null;
  probability: number | null;
  modelId: string | null;
  managedPrId: string | null;
  continued: {
    at: string;
    headSha: string;
    reviewStarted: boolean;
    byUserId: number | null;
  } | null;
};

type ReviewCardTarget = {
  repository?: string | null;
  pr_url?: string | null;
  pr_number?: number | null;
  head_sha?: string | null;
};

export function hasContinuedReviewCard(
  skipped: ReviewSkipped,
  cards: Iterable<ReviewCardTarget>,
): boolean {
  const continued = skipped.continued;
  if (skipped.skipReason !== "round_cap" || !continued?.reviewStarted) {
    return false;
  }

  return hasReviewCardForHead(skipped, continued.headSha, cards);
}

export function hasReviewCardForHead(
  skipped: ReviewSkipped,
  headSha: string,
  cards: Iterable<ReviewCardTarget>,
): boolean {
  return Array.from(cards).some((card) => {
    const prUrlMatch = card.pr_url?.match(/\/([^/]+\/[^/]+)\/pull\/(\d+)/);
    const repository = card.repository ?? prUrlMatch?.[1];
    const prNumber = card.pr_number ?? Number(prUrlMatch?.[2]);
    return (
      prNumber === skipped.prNumber &&
      repository?.toLowerCase() === skipped.repository.toLowerCase() &&
      card.head_sha?.toLowerCase() === headSha.toLowerCase()
    );
  });
}

const ReviewSkippedMetadata = z.object({
  repository: z.string(),
  pr_number: z.number(),
  pr_url: z.string(),
  head_sha: z.string().nullable().optional().default(null),
  kind: z.string().nullable().optional().default(null),
  requested_by_agent: z.boolean().optional().default(false),
  skip_reason: z.string(),
  reason: z.string().nullable().optional().default(null),
  probability: z.number().nullable().optional().default(null),
  model_id: z.string().nullable().optional().default(null),
  managed_pr_id: z.string().nullable().optional().default(null),
  continued: z
    .object({
      at: z.string(),
      head_sha: z.string(),
      review_started: z.boolean(),
      by_user_id: z.number().nullable(),
    })
    .nullable()
    .optional()
    .default(null),
});

const AGENT_DECIDED_REASON_LABELS: Record<string, string> = {
  trivial: "trivial change",
  formatting_only: "formatting-only push",
  generated_only: "generated files only",
};

// Mirrors ReviewSkipMirror's REASON_LABELS and ADMISSION_LABELS.
const START_FAILURE_LABELS: Record<string, string> = {
  rate_limited: "GitHub rate limit reached",
  start_busy: "another review start held the PR",
  dispatch_failed: "couldn't hand the review to [code]smith",
  error: "unexpected error",
};

const ADMISSION_DENIAL_LABELS: Record<string, string> = {
  codesmith_org_blocked: "organization usage is paused",
  codesmith_payment_blocked: "billing needs attention",
  codesmith_org_spend_limit_reached: "organization usage limit reached",
  codesmith_no_payment_method:
    "usage limit reached for organizations without a payment method",
};

export function reviewSkipLabel(
  skipReason: string,
  reason: string | null,
): string {
  if (skipReason.startsWith("agent_decided:")) {
    const modelReason = reason ?? skipReason.slice("agent_decided:".length);
    return (
      AGENT_DECIDED_REASON_LABELS[modelReason] ?? "not worth another round"
    );
  }

  switch (skipReason) {
    case "no_changes":
      return "no changes since the last review";
    case "formatting_only":
      return "formatting-only push";
    case "generated_only":
      return "generated files only";
    case "round_cap":
      return "round cap reached";
    case "failed_to_start":
      return START_FAILURE_LABELS[reason ?? ""] ?? "unexpected error";
    case "not_admitted":
      return (
        ADMISSION_DENIAL_LABELS[reason ?? ""] ??
        "organization cannot start reviews"
      );
    default:
      return "not worth another round";
  }
}

const parsedByRaw = new WeakMap<object, ReviewSkipped | null>();

export function parseReviewSkipped(message: {
  role: string;
  metadata?: Record<string, unknown> | null;
}): ReviewSkipped | null {
  if (message.role !== "agent") return null;
  const raw = message.metadata?.review_skipped;
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
    return null;
  }

  let parsed = parsedByRaw.get(raw);
  if (parsed === undefined) {
    const result = ReviewSkippedMetadata.safeParse(raw);
    parsed = result.success
      ? {
          repository: result.data.repository,
          prNumber: result.data.pr_number,
          prUrl: result.data.pr_url,
          headSha: result.data.head_sha,
          failedToStart: result.data.kind === "failed_to_start",
          requestedByAgent: result.data.requested_by_agent,
          skipReason: result.data.skip_reason,
          reason: result.data.reason,
          probability: result.data.probability,
          modelId: result.data.model_id,
          managedPrId: result.data.managed_pr_id,
          continued: result.data.continued
            ? {
                at: result.data.continued.at,
                headSha: result.data.continued.head_sha,
                reviewStarted: result.data.continued.review_started,
                byUserId: result.data.continued.by_user_id,
              }
            : null,
        }
      : null;
    parsedByRaw.set(raw, parsed);
  }
  return parsed;
}

export function isReviewSkippedMessage(message: {
  role: string;
  metadata?: Record<string, unknown> | null;
}): boolean {
  return parseReviewSkipped(message) !== null;
}
